import rawTimezones from '@/assets/csv/timezones.csv';
import rawLocations from '@/assets/csv/locations.csv';
import { SelectBox, SelectBoxItem, Text } from '@tremor/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { uniqBy } from 'lodash';
import { ConnectorTitle } from '../ConnectorTitle/ConnectorTitle';
import { ConnectionSteps } from '@/components/ConnectionSteps/ConnectionSteps';
import { DATA_FOR_SEO_CONNECTION_STEPS, NAV_ROUTES } from '@/utils/constants';
import { ConnectorSubtitle } from '../ConnectorSubtitle/ConnectorSubtitle';
import s from '@/pages/ConnectorInfo/ConnectorInfo.module.scss';
import { BottomActions } from '../BottomActions/BottomActions';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const locations = uniqBy(
  rawLocations as { location_code: string; location_name: string }[],
  'location_code',
);
const timezones = rawTimezones as { timezone: string }[];

export function DataForSeoInfoConnector() {
  const [isLoading, setIsLoading] = useState(false);
  const enableDataForSeoSchema = z.object({
    timezone: z
      .string()
      .min(1, 'Timezone is required')
      .refine((val) => {
        const timezone = timezones.find((tz) => tz.timezone === val);
        return !!timezone;
      }, 'Invalid timezone'),
    location: z.coerce
      .string()
      .min(1, 'Location is required')
      .refine((val) => {
        if (isNaN(parseInt(val)) || !Number.isInteger(parseInt(val))) return false;
        const location = locations.find((loc) => loc.location_code === val);
        return !!location;
      }, 'Invalid location'),
  });
  const navigate = useNavigate();

  type EnableDataForSeoSchema = z.infer<typeof enableDataForSeoSchema>;

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<EnableDataForSeoSchema>({
    resolver: zodResolver(enableDataForSeoSchema),
    defaultValues: {
      timezone: '',
      location: '',
    },
  });
  const onRegionChanged = async (e: string) => {
    setValue('timezone', e);
    await trigger('timezone');
  };
  const onLocationChanged = async (e: string) => {
    setValue('location', e);
    await trigger('location');
  };
  const onSubmit = async (data: EnableDataForSeoSchema) => {
    setIsLoading(true);
    const redirectUrl = new URL(window.location.href);
    redirectUrl.searchParams.set('timezone', data.timezone);
    redirectUrl.searchParams.set('location', data.location.toString());
    navigate({
      search: redirectUrl.search,
      pathname: NAV_ROUTES.connectorEdit('dataforseo'),
    });
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
      <ConnectorTitle connectorId="dataforseo">Data for SEO</ConnectorTitle>
      <ConnectionSteps
        style={{ marginBottom: '40px' }}
        steps={DATA_FOR_SEO_CONNECTION_STEPS}
        currentStep={1}
      />
      <ConnectorSubtitle>Setup Your Data for SEO connector</ConnectorSubtitle>
      <SelectBox placeholder="Choose Timezone" onValueChange={onRegionChanged}>
        {timezones.map(({ timezone }) => (
          <SelectBoxItem key={timezone} value={timezone} text={timezone} />
        ))}
      </SelectBox>
      <Text
        color="red"
        className={classNames({
          [s.errorVisible]: errors?.timezone,
          [s.errorHidden]: !errors?.timezone,
        })}
      >
        {errors?.timezone?.message || 'placeholder'}
      </Text>
      <SelectBox placeholder="Choose Location" onValueChange={onLocationChanged}>
        {locations.map(({ location_code, location_name }) => (
          <SelectBoxItem
            key={`${location_name}_${location_code}`}
            value={location_code}
            text={`${location_name} (${location_code})`}
          />
        ))}
      </SelectBox>
      <Text
        className={classNames({
          [s.errorVisible]: errors?.location,
          [s.errorHidden]: !errors?.location,
        })}
        color="red"
        style={{ color: 'red' }}
      >
        {errors?.location?.message}
      </Text>
      <BottomActions isCreating={true} isLoading={isLoading} />
    </form>
  );
}
